import React from 'react'
import { Tooltip } from '@mui/material'
import {
  Add as AddIcon, ArrowBack, ArrowForward, DarkMode as DarkModeIcon, LightMode as LightModeIcon, Search as SearchIcon,
  ContentCopy, VisibilityOutlined, VisibilityOffOutlined, OpenInNew,
} from '@mui/icons-material'
import { PrivatePage } from '../component/PagePrivate'
import { ViewToggle } from '../component/ViewToggle'
import { NotificationV2 } from '../container/common/NotificationV2'
import { ProfileV2 } from '../container/common/ProfileV2'
import { ConfirmV2 } from '../component/ConfirmV2'
import { api } from '../function/axios'
import { formatDate } from '../function/date'
import { useDispatch } from '../store'
import { toggleBlock } from '../store/user/actionCreator'
import { Subscriber } from '../store/user/reducer'
import { useAdminTheme } from '../hook/useAdminTheme'
import { CreateEnterpriseDialogV2 } from '../component/enterprise-v2/CreateEnterpriseDialogV2'
import l from './SubscribersPageV2.module.css'
import s from '../component/enterprise-v2/EnterpriseV2.module.css'

/*
 * Enterprise users in the v2 admin design. The classic page
 * (EnterprisePage.tsx) is untouched; this one reads the same list
 * (/users/clients?isEnterprise=true — in full, it is small, so the search
 * covers any part of a name, company, user id or key), blocks through the
 * same call and creates through the same request (CreateEnterpriseDialogV2).
 * A click on a row opens the subscriber page, as there.
 *
 * New on top: totals, companies as filters, the package and its balance,
 * when the user was last active, and the access key hidden until shown.
 */

const PER_PAGE = 12
type Row = Subscriber
type StatusFilter = 'all' | 'active' | 'blocked'

const nameOf = (r: Row) => [r.firstname, r.lastname].filter(Boolean).join(' ')
const initials = (r: Row) => (`${(r.firstname || r.companyName || 'E')[0]}${(r.lastname || '')[0] || ''}`).toUpperCase()
const lastActive = (r: Row): number | null => {
  const t = (r.sessions || []).map((x) => new Date(x.lastActiveDate).getTime()).filter((x) => !Number.isNaN(x))
  return t.length ? Math.max(...t) : null
}
function ago(ms: number): string {
  const d = Math.floor((Date.now() - ms) / 86400000)
  if (d <= 0) return 'today'
  if (d === 1) return 'yesterday'
  if (d < 30) return `${d} days ago`
  return formatDate(new Date(ms).toISOString(), 'dd.MM.yyyy')
}
const mask = (key: string) => (key.length > 8 ? `${key.slice(0, 4)}••••••••${key.slice(-4)}` : '••••••••')

function Avatar({ r }: { r: Row }) {
  const [broken, setBroken] = React.useState(false)
  return r.avatar && !broken
    ? <img className={s.avatar} src={r.avatar} alt="" onError={() => setBroken(true)} />
    : <span className={s.avatar}>{initials(r)}</span>
}

function KeyCell({ value }: { value: string }) {
  const [shown, setShown] = React.useState(false)
  const [copied, setCopied] = React.useState(false)
  return (
    <div className={s.key} onClick={(e) => e.stopPropagation()}>
      <code>{shown ? value : mask(value)}</code>
      <Tooltip title={shown ? 'Hide' : 'Show the key'}>
        <button type="button" className={s.iconSm} onClick={() => setShown(!shown)} aria-label={shown ? 'Hide' : 'Show the key'}>
          {shown ? <VisibilityOffOutlined sx={{ fontSize: 15 }} /> : <VisibilityOutlined sx={{ fontSize: 15 }} />}
        </button>
      </Tooltip>
      <Tooltip title={copied ? 'Copied' : 'Copy the key'}>
        <button
          type="button"
          className={s.iconSm}
          onClick={() => { void navigator.clipboard?.writeText(value).catch(() => undefined); setCopied(true); window.setTimeout(() => setCopied(false), 1300) }}
          aria-label="Copy the key"
        >
          <ContentCopy sx={{ fontSize: 14 }} />
        </button>
      </Tooltip>
    </div>
  )
}

export function EnterprisePageV2() {
  const dispatch = useDispatch()
  const { theme, toggleTheme } = useAdminTheme()

  const [rows, setRows] = React.useState<Row[] | null>(null)
  const [query, setQuery] = React.useState('')
  const [status, setStatus] = React.useState<StatusFilter>('all')
  const [company, setCompany] = React.useState<string | 'all'>('all')
  const [page, setPage] = React.useState(0)
  const [creating, setCreating] = React.useState(false)
  const [toBlock, setToBlock] = React.useState<Row | null>(null)

  const load = React.useCallback(async () => {
    try {
      const { data } = await api({ method: 'GET', url: '/users/clients', params: { isEnterprise: true, offset: 0, limit: 1000 } })
      const list: Row[] = Array.isArray(data) ? data : data?.list || []
      setRows(list.filter((r) => r.isEnterprise !== false))
    } catch {
      setRows([])
    }
  }, [])
  React.useEffect(() => { void load() }, [load])
  React.useEffect(() => { setPage(0) }, [query, status, company])

  const all = rows || []
  const companies = Array.from(new Set(all.map((r) => (r.companyName || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b))
  const monthAgo = Date.now() - 30 * 86400000
  const counts = {
    active: all.filter((r) => !r.isBlocked).length,
    blocked: all.filter((r) => r.isBlocked).length,
    recent: all.filter((r) => (lastActive(r) || 0) > monthAgo).length,
  }

  const q = query.trim().toLowerCase()
  const list = all
    .filter((r) => status === 'all' || (status === 'blocked' ? r.isBlocked : !r.isBlocked))
    .filter((r) => company === 'all' || (r.companyName || '').trim() === company)
    .filter((r) => !q || [nameOf(r), r.companyName, r.uid, r.email, r.phone, r.enterpriseAccessKey?.key].some((v) => (v || '').toLowerCase().includes(q)))
    .sort((a, b) => new Date(b.enterpriseAccessKey?.createdTime || b.createdTime).getTime() - new Date(a.enterpriseAccessKey?.createdTime || a.createdTime).getTime())
  const pages = Math.max(1, Math.ceil(list.length / PER_PAGE))
  const shown = list.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE)

  const open = (r: Row) => window.open(`/users/subscriber/${r.id}`, '_blank', 'noopener,noreferrer')

  const header = (
    <div className={`${l.theme} ${l.header}`} data-theme={theme}>
      <div className={l.crumb}><small>Users</small><b>Enterprise</b></div>
      <div className={l.spacer} />
      <label className={l.search}>
        <SearchIcon sx={{ fontSize: 17 }} />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, company, user id, key" />
      </label>
      <ViewToggle dark={theme === 'dark'} />
      <Tooltip title={theme === 'dark' ? 'Light theme' : 'Dark theme'}>
        <button className={l.iconBtn} onClick={toggleTheme} aria-label="Toggle theme">
          {theme === 'dark' ? <LightModeIcon sx={{ fontSize: 17 }} /> : <DarkModeIcon sx={{ fontSize: 17 }} />}
        </button>
      </Tooltip>
      <NotificationV2 />
      <ProfileV2 />
    </div>
  )

  return (
    <PrivatePage header={header}>
      <div className={`${l.theme} ${l.page}`} data-theme={theme}>
        <div className={`${l.kpis} ${s.kpis}`}>
          <div className={l.kpi}><small>Enterprise users</small><b>{rows ? all.length : '—'}</b><span>sign in with an access key</span></div>
          <div className={l.kpi}><small>Companies</small><b>{rows ? companies.length : '—'}</b><span>{rows ? `${all.filter((r) => !r.companyName).length} without a company` : ''}</span></div>
          <div className={l.kpi}><small>Active in 30 days</small><b style={{ color: 'var(--ok)' }}>{rows ? counts.recent : '—'}</b><span>signed in recently</span></div>
          <div className={l.kpi}><small>Blocked</small><b>{rows ? counts.blocked : '—'}</b><span>cannot sign in</span></div>
        </div>

        <div className={l.sec}>
          <div className={s.chips}>
            {([['all', 'All', all.length], ['active', 'Active', counts.active], ['blocked', 'Blocked', counts.blocked]] as const).map(([k, label, n]) => (
              <button key={k} type="button" className={`${l.chip} ${status === k ? l.chipOn : ''}`} onClick={() => setStatus(k)}>
                {label}{rows ? <span className={s.chipCount}>{n}</span> : null}
              </button>
            ))}
          </div>
          {companies.length > 0 && (
            <>
              <span className={s.sep} />
              <div className={s.chips}>
                <button type="button" className={`${l.chip} ${company === 'all' ? l.chipOn : ''}`} onClick={() => setCompany('all')}>All companies</button>
                {companies.slice(0, 12).map((c) => (
                  <button key={c} type="button" className={`${l.chip} ${company === c ? l.chipOn : ''}`} onClick={() => setCompany(c)}>{c}</button>
                ))}
              </div>
            </>
          )}
          <div className={l.spacer} />
          <button type="button" className={s.primary} onClick={() => setCreating(true)}><AddIcon sx={{ fontSize: 17 }} />New Enterprise user</button>
        </div>

        <div className={l.card}>
          <div className={l.scroll}>
            {rows === null ? (
              <div className={l.empty}>Loading…</div>
            ) : !shown.length ? (
              <div className={l.empty}>{q || status !== 'all' || company !== 'all' ? 'Nothing matches the filter' : 'No Enterprise users yet — create the first one'}</div>
            ) : (
              <table className={l.table}>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Company</th>
                    <th>Package</th>
                    <th>Access key</th>
                    <th>Key issued</th>
                    <th>Last active</th>
                    <th className={s.center}>Blocked</th>
                    <th aria-label="Open" />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => {
                    const seen = lastActive(r)
                    return (
                      <tr key={r.id} className={r.isBlocked ? s.rowBlocked : ''} onClick={() => open(r)} title="Open the user’s page">
                        <td>
                          <div className={s.person}>
                            <Avatar r={r} />
                            <div className={s.who}>
                              <b>{nameOf(r) || <span className={l.muted}>No name</span>}</b>
                              <span className={s.mono}>{r.uid}</span>
                            </div>
                          </div>
                        </td>
                        <td>{r.companyName ? <span className={s.tag}>{r.companyName}</span> : <span className={l.muted}>—</span>}</td>
                        <td>
                          {r.package ? (
                            <>
                              <span>{r.package.title}</span>
                              <span className={l.sub}>{Number(r.packageBalance || 0).toLocaleString()} credits left</span>
                            </>
                          ) : <span className={l.muted}>No package</span>}
                        </td>
                        <td>{r.enterpriseAccessKey?.key ? <KeyCell value={r.enterpriseAccessKey.key} /> : <span className={l.muted}>—</span>}</td>
                        <td className={l.nowrap}>
                          {r.enterpriseAccessKey?.createdTime ? (
                            <>{formatDate(r.enterpriseAccessKey.createdTime, 'dd.MM.yyyy')}<span className={l.sub}>{formatDate(r.enterpriseAccessKey.createdTime, 'HH:mm')}</span></>
                          ) : '—'}
                        </td>
                        <td className={l.nowrap}>{seen ? ago(seen) : <span className={l.muted}>Never</span>}</td>
                        <td className={s.center} onClick={(e) => e.stopPropagation()}>
                          <Tooltip title={r.isBlocked ? 'Unblock' : 'Block'}>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={r.isBlocked}
                              aria-label={r.isBlocked ? 'Unblock' : 'Block'}
                              className={`${s.switch} ${r.isBlocked ? s.switchDanger : ''}`}
                              onClick={() => setToBlock(r)}
                            >
                              <span />
                            </button>
                          </Tooltip>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Open the user’s page"><button type="button" className={s.act} onClick={() => open(r)} aria-label="Open the user’s page"><OpenInNew sx={{ fontSize: 15 }} /></button></Tooltip>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
          {pages > 1 && (
            <div className={s.pager}>
              <span>{page * PER_PAGE + 1}–{Math.min(list.length, (page + 1) * PER_PAGE)} of {list.length}</span>
              <div className={s.pagerBtns}>
                <button type="button" className={s.act} disabled={page === 0} onClick={() => setPage(page - 1)} aria-label="Previous page"><ArrowBack sx={{ fontSize: 16 }} /></button>
                <button type="button" className={s.act} disabled={page >= pages - 1} onClick={() => setPage(page + 1)} aria-label="Next page"><ArrowForward sx={{ fontSize: 16 }} /></button>
              </div>
            </div>
          )}
        </div>
      </div>

      {creating && <CreateEnterpriseDialogV2 theme={theme} companies={companies} onClose={() => setCreating(false)} onCreated={() => { void load() }} />}
      {toBlock && (
        <ConfirmV2
          header={toBlock.isBlocked
            ? `Unblock ${nameOf(toBlock) || toBlock.uid}? Their access key works again.`
            : `Block ${nameOf(toBlock) || toBlock.uid}? Their access key stops working.`}
          onYes={() => dispatch(toggleBlock(toBlock.id, !toBlock.isBlocked, () => { void load() }))}
          onClose={() => setToBlock(null)}
        />
      )}
    </PrivatePage>
  )
}
